import { ConfirmModal, TablePaginationContext, UnLink } from '@/components';
import { RoleColumn } from '@/components/Columns/RoleColumn';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { useDisassociateCompanyUser } from '@/modules/Companies/queries/disassociate-user';
import { useDisassociateOrganizationUser } from '@/modules/Organizations/queries/disassociate-user';
import { AdminPrivilege } from '@/modules/Users/domain/admin-privilege';
import { useUpdateUserAccountAdmin } from '@/modules/Users/queries';
import { useGetUserAdminPrivileges } from '@/modules/Users/queries/get-user-admin-privileges';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type UserAdminTabProps = {
  userId: number;
};

type EntityType = 'tenant' | 'organization' | 'company';

//TODO: REVIEW ON RBAC BRANCH
export const UserAdminTab = ({ userId }: UserAdminTabProps) => {
  const { t } = useTranslation();
  const { searchTerm, page, tableSort } = useContext(TablePaginationContext);
  const sort = tableSort[0];
  const [disassociationEntity, setDisassociationEntity] = useState<{
    type: EntityType;
    entityId: number;
  }>();
  const { data: adminPriviledges, isLoading } = useGetUserAdminPrivileges({
    userId,
    pageNumber: page,
    filter: searchTerm,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const { mutate: updateUserAcountAdmin } = useUpdateUserAccountAdmin();
  const { mutate: disassociateUserOrganization } =
    useDisassociateOrganizationUser();
  const { mutate: disassociateUserCompany } = useDisassociateCompanyUser();

  const columns = useMemo<SortableColumnDef<AdminPrivilege>[]>(
    () => [
      {
        id: 'tenant',
        name: t('tenant'),
        accessor: 'tenant',
      },
      {
        id: 'organization',
        name: t('organization'),
        accessor: 'organization',
      },
      {
        id: 'company',
        name: t('company'),
        accessor: 'company',
      },
      {
        id: 'role',
        name: t('role'),
        accessor: 'roleName',
        renderRow: (row) => (
          <RoleColumn roleName={row.roleName} roleEntity={row.roleEntity} />
        ),
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <UnLink
            className="cursor-pointer"
            onClick={() => {
              const entityId =
                row.tenantId || row.organizationId || row.companyId;

              const entityTypeMap: Record<EntityType, boolean> = {
                tenant: !!row.tenantId,
                organization: !!row.organizationId,
                company: !!row.companyId,
              };

              const entityType = Object.keys(entityTypeMap).find(
                (type) => entityTypeMap[type as EntityType],
              ) as EntityType;

              if (entityId && entityType) {
                setDisassociationEntity({
                  entityId,
                  type: entityType,
                });
              }
            }}
          />
        ),
      },
    ],
    [t],
  );

  const items = useMemo(() => {
    if (!adminPriviledges) {
      return adminPriviledges;
    }

    return {
      ...adminPriviledges,
      items: adminPriviledges?.items?.map((item, index) => ({
        ...item,
        id: `${adminPriviledges.currentPage}_${index}_${sort?.columnId ?? ''}_${sort?.direction ?? ''}`,
      })),
    };
  }, [adminPriviledges, sort?.columnId, sort?.direction]);

  const onConfirm = () => {
    if (disassociationEntity) {
      const entityTypeMap: Record<EntityType, () => void> = {
        tenant: () =>
          updateUserAcountAdmin({
            accountAdmin: false,
            userIds: [userId],
          }),
        organization: () =>
          disassociateUserOrganization({
            userId,
            organizationId: disassociationEntity.entityId,
          }),
        company: () =>
          disassociateUserCompany({
            userId,
            companyId: disassociationEntity.entityId,
          }),
      };

      entityTypeMap[disassociationEntity.type]();
    }

    setDisassociationEntity(undefined);
  };

  const confirmMessage: Record<EntityType, string> = {
    tenant: t('confirm_user_account_admin_disassociate'),
    organization: t('confirm_user_organization_disassociate'),
    company: t('confirm_user_company_disassociate'),
  };

  return (
    <>
      <PagedTable
        title={t('administration_privileges')}
        columns={columns}
        page={items}
        isLoading={isLoading}
        emptyStateMessage={t('no_admin_privileges_available')}
      />
      <ConfirmModal
        isOpen={!!disassociationEntity}
        title={t('please_confirm_action')}
        message={
          disassociationEntity ? confirmMessage[disassociationEntity.type] : ''
        }
        onConfirm={onConfirm}
        handleClose={() => setDisassociationEntity(undefined)}
      />
    </>
  );
};
